import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import config from '../utils/config';
import { claimUrls, ShortenedUrlResponse } from '../utils/sqrone-api';

interface User {
  id: string;
  email: string;
  name: string;
  provider: string;
  picture?: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for token in URL hash (after OAuth redirect)
    const hash = window.location.hash;
    if (hash.startsWith('#token=')) {
      const newToken = hash.substring(7);
      setToken(newToken);
      localStorage.setItem('auth_token', newToken);
      window.location.hash = ''; // Clear hash

      // Fetch user info with the new token
      fetchUserInfo(newToken);
    } else {
      // Load token from localStorage
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) {
        setToken(savedToken);
        fetchUserInfo(savedToken);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const claimAnonymousCheckpoints = async (authToken: string) => {
    try {
      const stored = localStorage.getItem('shortened-urls');
      if (!stored) return;

      const urls: ShortenedUrlResponse[] = JSON.parse(stored);
      if (urls.length === 0) return;

      const shortCodes = urls.map(u => u.shortCode);
      const result = await claimUrls(shortCodes, authToken);

      // Clear successfully claimed checkpoints from local storage
      if (result.claimed.length > 0) {
        const remaining = urls.filter(u => !result.claimed.includes(u.shortCode));
        if (remaining.length > 0) {
          localStorage.setItem('shortened-urls', JSON.stringify(remaining));
        } else {
          localStorage.removeItem('shortened-urls');
        }
        console.log(`Successfully claimed ${result.claimed.length} checkpoint(s)`);
      }

      if (result.failed.length > 0) {
        console.warn(`Failed to claim ${result.failed.length} checkpoint(s):`, result.failed);
      }
    } catch (error) {
      // Don't fail login if claiming fails - just log it
      console.error('Failed to claim anonymous checkpoints:', error);
    }
  };

  const fetchUserInfo = async (authToken: string) => {
    try {
      const response = await fetch(`${config.authServiceUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.userId || data.id,
          email: data.email,
          name: data.name,
          provider: data.provider || 'Unknown',
          picture: data.profilePictureUrl || data.picture || data.avatar,
        });

        // FS10: Claim any anonymous checkpoints after successful login
        await claimAnonymousCheckpoints(authToken);
      } else {
        // Token is invalid, clear it
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // Redirect to auth service sign-in page with enabled providers and app_id
    const redirectUri = encodeURIComponent(config.appUrl);
    const providers = encodeURIComponent(config.authProviders);
    window.location.href = `${config.authServiceUrl}/signin?redirect_uri=${redirectUri}&providers=${providers}&app_id=${config.appId}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    // FS12: Redirect to Home on sign out
    window.location.href = '/';
  };

  const value: AuthContextType = {
    token,
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
